import { transition } from '@helpers/animate';
import borderRadius from '@helpers/borderRadius';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { baseGrid } from '@helpers/grid';
import { between, Device, from, until } from '@helpers/media';
import { contentStyles, displayStyles, linkOpposite, paragraphExtraSmall, } from '@helpers/typography';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const Footer = styled.footer `
  overflow: hidden;
`;
const TopBorder = styled.div `
  width: 100%;
  height: ${fluid(12, 16)};
  background-color: ${brand.orange.base};
  border-bottom: ${fluid(6, 8)} solid ${brand.global.white};
  box-sizing: content-box;
`;
const Container = styled(SectionWrapper) ``;
const UpperFooter = styled.div `
  ${baseGrid};
  position: relative;
  align-items: start;
  padding-bottom: ${fluid(40, 62)};
  border-bottom: 2px solid ${brand.grey.grey33};

  @media ${until(Device.Tablet)} {
    grid-template-columns: auto;
    border-bottom: none;

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      height: 2px;
      width: 100vw;
      background-color: ${brand.grey.grey33};
    }
  }
`;
const UpperFooterLeft = styled.div `
  display: flex;
  justify-content: space-between;
  column-gap: 10px;

  @media ${from(Device.Tablet)} {
    grid-column: span 4;
  }

  @media ${between(Device.Tablet, Device.DesktopLarge)} {
    flex-direction: column;
  }
`;
const UpperFooterLeftContent = styled.div `
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;
const LogoWrapper = styled.a ``;
const Logo = styled.img `
  max-width: ${fluid(122, 162)};
  height: auto;
`;
const CallUs = styled.div `
  ${displayStyles};
  margin: ${fluid(12, 16)} 0 0;

  @media ${from(Device.Tablet)} {
    padding-bottom: 16px;
  }
`;
const CallUsLabel = styled.span `
  color: ${brand.global.white};
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(12, 16)};
`;
const CallUsLink = styled.a `
  display: flex;
  align-items: center;
  column-gap: ${fluid(6, 8)};
  text-decoration: none;

  svg {
    position: relative;
    top: 2px;
    color: ${brand.orange.base};
    width: ${fluid(16, 18)};
    height: ${fluid(16, 18)};
  }
`;
const CallUsNumber = styled.span `
  color: ${brand.tealAccessibleLight._1000};
  font-size: ${fluid(20, 28)};
  line-height: ${fluid(20, 28)};
`;
const SocialMediaLinksWrapper = styled.div `
  margin-top: auto;

  @media ${until(Device.Tablet)} {
    display: none;
  }
`;
const SupportingImageWrapper = styled.div `
  @media ${between(Device.Tablet, Device.DesktopLarge)} {
    margin-top: 16px;
  }
`;
const SupportingImage = styled.img ``;
const UpperFooterRight = styled.div `
  margin-top: 40px;

  @media ${from(Device.Tablet)} {
    grid-column: 6 / span 7;
    margin-top: 0;
  }
`;
const UsefulLinksHeading = styled.h2 `
  ${displayStyles};

  font-size: 24px;
  line-height: 28px;
  margin-bottom: 16px;
  color: ${brand.tealAccessibleLight._1000};

  @media ${until(Device.Tablet)} {
    display: none;
  }
`;
const footerLinkStyles = css `
  ${linkOpposite};

  @media ${from(Device.TabletLarge)} {
    ${fonts.aptos.extraBold};
    --textLinkColour: ${brand.global.white};
  }
`;
const LinkListContainer = styled.div `
  display: flex;
  flex-direction: column;
  row-gap: 16px;

  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${fluid(28, 80)};
  }
`;
const LinkListGroup = styled.div `
  @media ${until(Device.TabletLarge)} {
    background-color: ${brand.orange._100};
    border-radius: ${borderRadius.xs}px;
    padding: 16px;
  }

  &[data-mobile-only] {
    display: block;

    @media ${from(Device.TabletLarge)} {
      display: none;
    }
  }
`;
const LinkListHeader = styled.button `
  ${ButtonReset};

  display: block;

  h2 {
    ${displayStyles};

    font-size: 16px;
    line-height: 16px;
    margin: 0;
  }

  button& {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    gap: 24px;
    justify-content: space-between;

    &:focus-visible {
      outline: auto;
      outline-offset: 5px;
      outline-width: 1px;
    }

    &[data-open='true'] svg {
      transform: rotate(180deg);
    }

    /* Mobile click styles - button should mimic press */
    @media ${until(Device.TabletLarge)} {
      ${transition('transform', 0.15)};

      &:active {
        transform: translateY(1.5px);
      }
    }

    @media ${from(Device.TabletLarge)} {
      display: none;
    }
  }

  a& {
    ${footerLinkStyles};
  }

  a&,
  span& {
    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }

  svg {
    ${transition('transform', 0.25)};

    display: block;
    width: 14px;
    height: 8px;
    color: ${brand.orange.base};
  }
`;
const LinkList = styled(m.ul) `
  display: flex;
  flex-direction: column;
  overflow: hidden;

  li {
    margin-top: 16px;
    ${fonts.aptos.regular};

    font-size: ${fluid(14, 16)};
    line-height: ${fluid(16, 20)};

    svg {
      ${transition('color')};
      position: relative;
      flex-shrink: 0;
      width: 12px;
      height: 12px;

      @media ${from(Device.TabletLarge)} {
        top: 1px;
        width: 6px;
        height: 10px;
        color: ${brand.orange._400};
      }
    }
  }

  a {
    ${footerLinkStyles};

    display: flex;
    justify-content: space-between;
    column-gap: 8px;

    &:hover {
      svg {
        color: ${brand.tealAccessibleLight._1000};
      }
    }

    div {
      &[data-mobile-only] {
        display: block;

        @media ${from(Device.TabletLarge)} {
          display: none;
        }
      }

      &[data-desktop-only] {
        display: none;

        @media ${from(Device.TabletLarge)} {
          display: block;
        }
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    height: auto !important;
    padding-bottom: 3px;
    margin-bottom: 0 !important;
    visibility: visible !important;

    li[data-mobile-only] {
      display: none;
    }
  }
`;
const LowerFooter = styled.div `
  display: flex;
  flex-direction: column;
  padding-top: ${fluid(40, 62)};

  @media ${from(Device.Tablet)} {
    flex-direction: row;
    justify-content: space-between;
    column-gap: ${fluid(24, 32)};
  }
`;
const LowerFooterLeft = styled.div `
  @media ${from(Device.Tablet)} {
    max-width: 544px;
  }
`;
const legalTextStyles = css `
  ${contentStyles};

  p {
    ${paragraphExtraSmall};
    margin-top: ${fluid(24, 32)};
  }

  @media ${until(Device.Tablet)} {
    text-align: center;
  }
`;
const LegalTextBody = styled.div `
  ${legalTextStyles};
`;
const CopyrightText = styled.div `
  ${legalTextStyles};
  margin-top: ${fluid(24, 32)};
`;
const LowerFooterRight = styled.div `
  order: -1;
  margin-bottom: 40px;

  @media ${from(Device.Tablet)} {
    order: unset;
    display: flex;
    flex-direction: column;
    align-items: end;
    row-gap: ${fluid(24, 32)};
    margin-bottom: 0;
    min-width: 365px;
  }
`;
const logoStyles = css `
  width: auto;
  max-height: 72px;

  @media ${until(Device.Tablet)} {
    max-height: 56px;
  }
`;
const SRALogo = styled.img `
  ${logoStyles};
`;
const AffiliateLogos = styled.div `
  display: flex;
  align-items: center;
  column-gap: ${fluid(24, 32)};

  @media ${until(Device.Tablet)} {
    justify-content: center;

    ${SRALogo} {
      display: none;
    }
  }
`;
const AffiliateLogo = styled.img `
  ${logoStyles};
`;
const LegalLinks = styled.ul `
  display: flex;
  column-gap: 18px;
  margin-top: auto;

  @media ${until(Device.Tablet)} {
    display: none;
  }
`;
const LegalLink = styled.a `
  ${linkOpposite};
  ${fonts.aptos.extraBold};

  color: ${brand.global.white};
  font-size: 14px;
  line-height: 22px;
`;
const LowerFooterMobile = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;

  @media ${from(Device.Tablet)} {
    display: none;
  }

  ${SocialMediaLinksWrapper} {
    display: block;
    margin-top: 0;
  }
`;
const FooterStyles = {
    AffiliateLogo,
    AffiliateLogos,
    CallUs,
    CallUsLabel,
    CallUsLink,
    CallUsNumber,
    CopyrightText,
    Container,
    Footer,
    LegalLink,
    LegalLinks,
    LegalTextBody,
    LinkList,
    LinkListContainer,
    LinkListGroup,
    LinkListHeader,
    Logo,
    LogoWrapper,
    LowerFooter,
    LowerFooterLeft,
    LowerFooterMobile,
    LowerFooterRight,
    SocialMediaLinksWrapper,
    SRALogo,
    SupportingImage,
    SupportingImageWrapper,
    TopBorder,
    UpperFooter,
    UpperFooterLeft,
    UpperFooterLeftContent,
    UpperFooterRight,
    UsefulLinksHeading,
};
export default FooterStyles;
